import {Component, computed, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {Checkpoint, level as LevelEnum} from '../../classes/checkpoint';
import {CheckpointService} from '../../services/checkpoint.service';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatSuffix} from '@angular/material/form-field';
import {NgOptionComponent, NgSelectComponent} from '@ng-select/ng-select';
import {Platform} from '../../classes/platform';
import {Utils} from '../../utils.class';
import {PlatformService} from '../../services/platform.service';
import {Routenames} from '../../classes/route-name.enum';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {MsReadablePipe} from '../../pipes/milliseconds-to-readable-time.pipe';
import {BytesPipePipe} from '../../pipes/bytes.pipe';

const isFilled = (value: unknown) => value !== null && value !== undefined && value !== '';

@Component({
    selector: 'app-checkpoint',
    templateUrl: './checkpoint-store.component.html',
    styleUrls: ['./checkpoint-store.component.scss'],
    host: {class: 'section-page'},
    imports: [
        FormsModule,
        MatFormField,
        MatInput,
        NgSelectComponent,
        ReactiveFormsModule,
        NgOptionComponent,
        FieldErrorComponent,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        BreadcrumbComponent,
        HasFeaturePipe,
        MatIcon,
        MatIconButton,
        MatSuffix,
        MsReadablePipe
    ]
})
export class CheckpointStoreComponent extends CodaltComponent implements OnInit {
    readonly form = signal<FormGroup | null>(null);
    readonly platforms = signal<Platform[]>([]);
    readonly values = signal<Checkpoint | null>(null);
    private readonly msReadable = new MsReadablePipe();
    private readonly bytesReadable = new BytesPipePipe();
    private readonly formatMs = (value: number) => this.msReadable.transform(+value || 0);
    private readonly formatBytes = (value: number) => this.bytesReadable.transform(+value || 0);

    readonly summary = computed(() => {
        const v = this.values();
        if (!v) {
            return [];
        }
        const parts: {text: string, bold?: boolean}[] = [];
        const text = (value: string) => parts.push({text: value});
        const bold = (value: string) => parts.push({text: value, bold: true});

        if (isFilled(v.external_ip_allowed)) {
            text('Dit checkpoint wordt niet zelf opgevraagd, maar ontvangt resultaten via externe logging vanaf ');
            bold(v.external_ip_allowed);
            text('.');
            return parts;
        }

        text('Dit checkpoint vraagt ');
        bold(`elke ${this.formatMs(v.interval)}`);
        text(' de URL ');
        bold(v.url || '(nog geen URL)');
        text(' op');
        if (isFilled(v.valid_response_code)) {
            text(' en verwacht statuscode ');
            bold(`${v.valid_response_code}`);
        }
        if (v.validate_ssl) {
            text(' met een geldig SSL-certificaat');
        }
        text('.');

        if (isFilled(v.timeout)) {
            text(' Duurt het langer dan ');
            bold(this.formatMs(v.timeout));
            text(' of gaat het mis, dan volgt een melding op ');
            bold(v.level);
            if (+v.retries > 0 && isFilled(v.retry_delay)) {
                text(' en probeert het ');
                bold(`${v.retries} keer`);
                text(' opnieuw met ');
                bold(this.formatMs(v.retry_delay));
                text(' ertussen');
            }
            text('.');
        }

        const sizeMin = isFilled(v.min_content_size_error);
        const sizeMax = isFilled(v.max_content_size_error);
        const timeMax = isFilled(v.max_response_time_error);
        if (sizeMin || sizeMax || timeMax) {
            text(' De respons moet ');
            if (sizeMin && sizeMax) {
                text('tussen ');
                bold(this.formatBytes(v.min_content_size_error));
                text(' en ');
                bold(this.formatBytes(v.max_content_size_error));
                text(' groot zijn');
            } else if (sizeMin || sizeMax) {
                text(sizeMin ? 'minimaal ' : 'maximaal ');
                bold(this.formatBytes(sizeMin ? v.min_content_size_error : v.max_content_size_error));
                text(' groot zijn');
            }
            if (timeMax) {
                text(sizeMin || sizeMax ? ' en binnen ' : 'binnen ');
                bold(this.formatMs(v.max_response_time_error));
                text(' binnen zijn');
            }
            text('.');
        }
        return parts;
    });

    readonly timelineTotal = computed(() => {
        const v = this.values();
        const timeout = +v?.timeout || 0;
        return timeout + (+v?.retries || 0) * ((+v?.retry_delay || 0) + timeout);
    });

    readonly ranges = computed(() => {
        const v = this.values() as Record<string, any> | null;
        const fields = (prefix: string, format: (value: number) => string) => [
            {key: `min_${prefix}_error`, label: 'Error onder', filled: 'Kleiner dan {v} = error', empty: 'Geen ondergrens'},
            {key: `min_${prefix}_warning`, label: 'Waarschuwing onder', filled: 'Kleiner dan {v} = waarschuwing', empty: 'Niet ingesteld'},
            {key: `max_${prefix}_warning`, label: 'Waarschuwing boven', filled: 'Groter dan {v} = waarschuwing', empty: 'Niet ingesteld'},
            {key: `max_${prefix}_error`, label: 'Error boven', filled: 'Groter dan {v} = error', empty: 'Geen bovengrens'}
        ].map(field => ({
            key: field.key,
            label: field.label,
            help: isFilled(v?.[field.key]) ? field.filled.replace('{v}', format(v[field.key])) : field.empty
        }));
        return [
            {
                nr: 5,
                icon: 'fa-file',
                title: 'Grootte van de respons',
                description: 'Een respons die veel kleiner of groter is dan normaal wijst vaak op een fout, ook als de statuscode klopt.',
                unit: 'bytes',
                fields: fields('content_size', this.formatBytes)
            },
            {
                nr: 6,
                icon: 'fa-gauge',
                title: 'Reactietijd',
                description: 'Hoe snel de server moet antwoorden. Te traag geeft een waarschuwing of error.',
                unit: 'ms',
                fields: fields('response_time', this.formatMs)
            }
        ];
    });
    checkpointLevels = LevelEnum;
    minDate: Date;
    platformId: number;
    saving = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointService: CheckpointService,
        private confirmDialog: ConfirmDialogService,
        private platformService: PlatformService,
        private router: Router
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(this.platformService.getPlatforms().subscribe(platforms => {
            this.platforms.set(platforms);
            this.crumbs = [
                {name: 'Seire', route: Routenames.seire},
                {name: `Platform : ${this.platforms().find(platform => platform.id === this.platformId)?.name}`, route: `${Routenames.seire}/${this.platformId}`},
                {name: `Checkpoint : ${this.form().get('name').value ?? 'new'}`, route: `${Routenames.seire}/${this.platformId}/store/${this.form().get('id').value ?? 'new'}`}
            ];
        }));

        this.subscriptions.add(this.activatedRoute.params.subscribe((params: {platformId, checkId}) => {
            this.platformId = +params.platformId;
            if (params.checkId === 'new') {
                this.createForm(new Checkpoint());
            } else {
                this.subscriptions.add(
                    this.checkpointService.getCheckpoint(+params.checkId).subscribe({
                        next: res => this.createForm(res),
                        error: err => console.error(err)
                    })
                );
            }
        }));
    }

    createForm(checkpoint: Checkpoint) {
        const startDate = new Date();
        Utils.setTime(startDate, 0, 0, 0);
        const endDate = new Date(startDate);
        Utils.setTime(endDate, 23, 59, 0);

        if (!checkpoint.id || !Utils.isPast(checkpoint.start_date)) {
            this.minDate = new Date(startDate);
            this.minDate.setDate(this.minDate.getDate() - 100);
        } else {
            this.minDate = new Date(checkpoint.start_date);
        }

        const form = new FormGroup({
            id: new FormControl(checkpoint.id),
            platform_id: new FormControl(checkpoint.platform_id ?? this.platformId, Validators.required),
            responsible_user_id: new FormControl(checkpoint.responsible_user_id),
            fallback_responsible_user_id: new FormControl(checkpoint.fallback_responsible_user_id),
            url: new FormControl(checkpoint.url),
            start_date: new FormControl(checkpoint.start_date ? new Date(checkpoint.start_date) : startDate, Validators.required),
            end_date: new FormControl(checkpoint.end_date ? new Date(checkpoint.end_date) : checkpoint.end_date),
            name: new FormControl(checkpoint.name, Validators.required),

            validate_ssl: new FormControl(checkpoint.validate_ssl ?? true),
            valid_response_code: new FormControl(checkpoint.valid_response_code ?? 200, Validators.required),
            warning_response_code: new FormControl(checkpoint.warning_response_code),
            level: new FormControl(checkpoint.level ?? LevelEnum.PRODUCTION, Validators.required),
            retries: new FormControl(checkpoint.retries ?? 3, Validators.required),
            retry_delay: new FormControl(checkpoint.retry_delay ?? 30000, Validators.required),
            min_content_size_error: new FormControl(checkpoint.min_content_size_error ?? 10, Validators.required),
            max_content_size_error: new FormControl(checkpoint.max_content_size_error ?? 500000, Validators.required),
            min_content_size_warning: new FormControl(checkpoint.min_content_size_warning),
            max_content_size_warning: new FormControl(checkpoint.max_content_size_warning),
            timeout: new FormControl(checkpoint.timeout ?? 10000, Validators.required),
            interval: new FormControl(checkpoint.interval ?? 60000, Validators.required),
            valid_checksum: new FormControl(checkpoint.valid_checksum),
            min_response_time_error: new FormControl(checkpoint.min_response_time_error ?? 10, Validators.required),
            max_response_time_error: new FormControl(checkpoint.max_response_time_error ?? 10000, Validators.required),
            min_response_time_warning: new FormControl(checkpoint.min_response_time_warning),
            max_response_time_warning: new FormControl(checkpoint.max_response_time_warning),
            save_contents_on_success: new FormControl(checkpoint.save_contents_on_success ?? false),
            external_ip_allowed: new FormControl(checkpoint.external_ip_allowed),
            updated_at: new FormControl(checkpoint.updated_at)
        });
        this.form.set(form);
        const updateValues = () => this.values.set(Object.assign(new Checkpoint(), form.getRawValue()));
        updateValues();
        this.subscriptions.add(form.valueChanges.subscribe(updateValues));
    }

    cancel() {
        this.router.navigate([Routenames.seire, this.platformId]);
    }

    openDelete(event) {
        event.preventDefault();
        event.stopPropagation();

        const checkpoint = new Checkpoint();
        Object.assign(checkpoint, this.form().value);

        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet u zeker dat u ${checkpoint.name} wilt verwijderen?`,
                'Verwijderen',
                'Behouden'
            )
            .then(() => {
                this.checkpointService.deleteCheckpoint(checkpoint.id).then(() => {
                    this.router.navigate([Routenames.seire, this.platformId]);
                });
            })
            .catch(() => {
                console.log('checkpoint not deleted');
            });
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const checkpoint = new Checkpoint();
            Object.assign(checkpoint, this.form().value);

            if (checkpoint.external_ip_allowed === '') {
                checkpoint.external_ip_allowed = null;
            }

            this.subscriptions.add(
                this.checkpointService.saveCheckpoint(checkpoint).subscribe(() => {
                    this.router.navigate([Routenames.seire, checkpoint.platform_id]);
                })
            );
        }
    }
}
